import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Sparkles } from 'lucide-react';

import JobSection from '../../../containers/Browse/OpeningsListElement/JobSection';
import { selectCurrentUser } from '../../../selectors/authSelectors';
import {
  selectHotJobs,
  selectRecommendedJobs,
  selectReferralRecommendations,
  selectReferralRecommendationsTotal,
} from '../../../selectors/dashboardSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';

import SeeMoreButton from '../../shared/SeeMoreButton/SeeMoreButton';
import { ContactImportCard } from '../ContactImportCard/ContactImportCard';
import { RecommendationItem } from '../RecommendationItem/RecommendationItem';

import styles from './MobileJobFeed.module.scss';

export const MobileJobFeed = () => {
  const { t } = useTranslation(['common', 'dashboard']);
  const [displayCount] = useState(2);
  const [recommendedJobsDisplayCount] = useState(3);
  const user = useSelector(selectCurrentUser);
  const referralRecommendations = useSelector(selectReferralRecommendations);
  const referralRecommendationsTotal = useSelector(selectReferralRecommendationsTotal);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const hotJobs = useSelector(selectHotJobs);
  const recommendedJobs = useSelector(selectRecommendedJobs);

  const aiRecommendationsEnabled = currentOrganization?.ai_recommendations_for_members !== false;
  const [activeTab, setActiveTab] = useState(aiRecommendationsEnabled ? 'referrals' : 'jobs');

  const recommendations =
    referralRecommendationsTotal > 0 && referralRecommendations.some((recommendation) => recommendation.job_data);

  const renderReferralsSection = () => {
    if (user?.contacts_count === 0 || !recommendations) {
      return <ContactImportCard contacts_count={user?.contacts_count} recommendations={!recommendations} />;
    }

    return (
      <div className="space-y-4">
        {referralRecommendations?.length > 0 &&
          referralRecommendations
            .filter((recommendation) => recommendation.job_data)
            .slice(0, displayCount)
            .map((recommendation, index) => (
              <RecommendationItem
                key={recommendation.id || `recommendation-${index}`}
                recommendation={recommendation}
                currentOrganization={currentOrganization}
              />
            ))}
      </div>
    );
  };

  return (
    <>
      {/* Tabs for Navigation */}
      <div className="flex gap-3 px-3">
        {aiRecommendationsEnabled && (
          <div
            role="button"
            tabIndex={0}
            className={classNames(styles.headerButton, 'flex items-center gap-1.5', {
              [styles.headerButtonSelected]: activeTab === 'referrals',
            })}
            onClick={() => setActiveTab('referrals')}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTab('referrals')}
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            {t('dashboard:referralsForYou')}
          </div>
        )}
        <div
          role="button"
          tabIndex={0}
          className={classNames(styles.headerButton, 'flex items-center gap-1.5', {
            [styles.headerButtonSelected]: activeTab === 'jobs',
          })}
          onClick={() => setActiveTab('jobs')}
          onKeyDown={(e) => e.key === 'Enter' && setActiveTab('jobs')}
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          {t('dashboard:recommendedJobs')}
        </div>
      </div>

      {/* Referrals Section */}
      {activeTab === 'referrals' && (
        <div className="px-4">
          <div>
            <div className="mt-3">
              {renderReferralsSection()}
              {referralRecommendationsTotal > 0 && recommendations && displayCount < referralRecommendationsTotal && (
                <div className="flex items-center justify-center mt-3">
                  <SeeMoreButton label={t('buttons.seeMore')} path="/recommendations" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Jobs Section */}
      {activeTab === 'jobs' && (
        <div className="px-3">
          {recommendedJobs?.job_recommendations?.length > 0 ? (
            <div>
              <div className="space-y-4 mt-3">
                {recommendedJobs.job_recommendations.slice(0, recommendedJobsDisplayCount).map((job, index) => (
                  <div
                    key={job.job_id ? `job-${job.job_id}` : `job-${index}`}
                    className={styles.openingItemParent}
                    role="presentation"
                  >
                    <JobSection job={job.job_data} matchScore={job.match_score} />
                  </div>
                ))}
              </div>
              {recommendedJobs.total_count > recommendedJobsDisplayCount && (
                <div className="flex items-center justify-center mt-3">
                  <SeeMoreButton label={t('buttons.seeMore')} path="/recommended-jobs" />
                </div>
              )}
            </div>
          ) : (
            hotJobs?.jobs?.length > 0 && (
              <div>
                <div className="space-y-4 mt-3">
                  {hotJobs.jobs.slice(0, recommendedJobsDisplayCount).map((job, index) => (
                    <div key={job.id || `hot-job-${index}`} className={styles.openingItemParent} role="presentation">
                      <JobSection job={job} />
                    </div>
                  ))}
                </div>
                {hotJobs.jobs.length > recommendedJobsDisplayCount && (
                  <div className="flex items-center justify-center mt-3">
                    <SeeMoreButton label={t('buttons.seeMore')} path="/jobs" />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}
    </>
  );
};
